import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { CreateMasterProductPayload, MasterProduct } from './types';
import { masterProductsEndpoint } from './use-get-master-products-api';

type TData = MasterProduct;
type TError = AxiosError;
type TVariables = CreateMasterProductPayload;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// POST api/v1/cms/master-products
export function useCreateMasterProductApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async (data: TVariables): Promise<TData> => {
    // The API wraps the resource in an envelope: { data: {...} } — see
    // use-get-master-product-api.ts for the same fix on the GET-by-id side.
    const response = await http.post(masterProductsEndpoint, data);
    return response.data.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [masterProductsEndpoint],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([masterProductsEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
